import React from 'react';
import { useSelector } from 'react-redux';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import useStyles, { ImportFormLabel, OrbitReverseCheckbox, ImportControl } from '../styles';
import { Button, FormControl, FormGroup, InputLabel, MenuItem, Select } from '@mui/material';
import ZonePreviews from './zonePreviews';
import InfoPopup from './infoPopup';

const ImportHeader = ({
    canSave,
    inverse,
    hasImages,
    imageResize,
    trimPositions,
    framesPerSecond,
    trimPreviewArray,
    handleSave,
    handleSmoothnessChange,
    handleImageResizeChange,
    handleOrbitReverseChange,
}) => {
    const theme = useSelector(selectDefaultTheme);
    const classes = useStyles(theme);
    const smoothnessOptions = {
        Low: { id: 1, label: 'Low', value: 2 },
        Medium: { id: 2, label: 'Medium', value: 3 },
        High: { id: 3, label: 'High', value: 4 },
    };
    const imageResizeOptions = {
        None: { id: 1, label: 'None', value: 0 },
        Small: { id: 2, label: 'Small', value: 1 },
        Medium: { id: 3, label: 'Medium', value: 2 },
        Large: { id: 4, label: 'Large', value: 3 },
    };

    return (
        <div className={classes.importHeaderContainer}>
            <div className={classes.importHeaderContainer}>
                {hasImages != null && (
                    <div className={classes.importSettingsGroup}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {!hasImages && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        margin: '10px 10px 0px 10px',
                                    }}
                                >
                                    <ImportControl
                                        style={{
                                            width: '120px',
                                        }}
                                    >
                                        <InputLabel id="smoothness-id">Smoothness</InputLabel>
                                        <Select
                                            aria-label="smoothness"
                                            labelId="smoothness-id"
                                            margin="dense"
                                            size="small"
                                            label="Smoothness"
                                            placeholder="Smoothness"
                                            value={framesPerSecond.toString()}
                                            onChange={handleSmoothnessChange}
                                        >
                                            {Object.keys(smoothnessOptions)?.length > 0 &&
                                                Object.keys(smoothnessOptions).map((val) => (
                                                    <MenuItem
                                                        key={`smoothness-${smoothnessOptions[val].id}`}
                                                        value={smoothnessOptions[val].id}
                                                    >
                                                        {smoothnessOptions[val].label}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </ImportControl>
                                    <InfoPopup helpText="Smoothness will control how many intermediate frames are used while rotating between zones. Higher smoothness equals more frames." />
                                </div>
                            )}

                            {hasImages && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        margin: '10px 10px 0px 10px',
                                    }}
                                >
                                    <ImportControl
                                        style={{
                                            width: '120px',
                                        }}
                                    >
                                        <InputLabel id="image-resize-id">Image Resize</InputLabel>
                                        <Select
                                            style={{
                                                margin: 0,
                                                alignItems: 'flex-end',
                                            }}
                                            aria-label="Image Resize"
                                            labelId="image-resize-id"
                                            margin="dense"
                                            size="small"
                                            label="Image Resize"
                                            placeholder="Image Resize"
                                            value={imageResize}
                                            onChange={handleImageResizeChange}
                                        >
                                            {Object.keys(imageResizeOptions)?.length > 0 &&
                                                Object.keys(imageResizeOptions).map((val) => (
                                                    <MenuItem
                                                        key={`imageResizes-${imageResizeOptions[val].id}`}
                                                        value={imageResizeOptions[val].value}
                                                    >
                                                        {imageResizeOptions[val].label}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </ImportControl>
                                    <InfoPopup
                                        helpText={
                                            <>
                                                <p>
                                                    Image Resize will convert the images to the following maximum width
                                                    or height:
                                                </p>
                                                <br />
                                                <table>
                                                    <tbody>
                                                        <tr>
                                                            <td>Small</td>
                                                            <td>&nbsp;= 512</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Medium</td>
                                                            <td>&nbsp;= 1024</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Large</td>
                                                            <td>&nbsp;= 1280</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </>
                                        }
                                    />
                                </div>
                            )}

                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <FormControl>
                                    <FormGroup aria-label="position" row>
                                        <ImportFormLabel
                                            style={{
                                                marginLeft: '10px',
                                                marginRight: '0px',
                                            }}
                                            label="CCW Rotation"
                                            labelPlacement="start"
                                            control={
                                                <OrbitReverseCheckbox
                                                    checked={inverse}
                                                    onChange={handleOrbitReverseChange}
                                                />
                                            }
                                        ></ImportFormLabel>
                                    </FormGroup>
                                </FormControl>
                                <InfoPopup
                                    helpText={
                                        hasImages
                                            ? 'Select CCW Rotation if the orbit images were collected counter clockwise around the asset.'
                                            : 'Select CCW Rotation if the orbit video was captured counter clockwise around the asset.'
                                    }
                                />
                            </div>
                        </div>
                        {trimPositions && !hasImages && (
                            <ZonePreviews
                                title="Trim Positions"
                                previews={trimPreviewArray}
                                className={classes.trimZonePreviews}
                                isTrimPositions={true}
                            />
                        )}
                    </div>
                )}
                {hasImages == null && (
                    <div className={classes.fileDescriptionDiv}>
                        <p>
                            Multiple images or a single video may be used to create orbits. If using images, the image
                            filenames must contain at least one numeric character and should be in sequence.
                            <br />
                            <br />
                            Some valid sets of examples are:
                        </p>
                        <ul
                            style={{
                                listStyle: 'inside',
                            }}
                        >
                            <li>1.jpg, 2.jpg, 3.jpg, etc.</li>
                            <li>a1.jpg, a2.jpg, a3.jpg, etc.</li>
                            <li>a1.1.jpg, a1.2.jpg, a1.3.jpg, etc.</li>
                        </ul>
                    </div>
                )}
            </div>

            <Button
                variant="contained"
                color="primary"
                style={{ alignSelf: 'flex-start' }}
                disabled={!canSave}
                onClick={handleSave}
            >
                Save
            </Button>
        </div>
    );
};

export default ImportHeader;
